import { useState } from "react";
import { CONTACT } from "../../data/boats";
import { Mail, Phone, MapPin, ArrowRight, Send, CheckCircle, AlertCircle } from "lucide-react";

// ─── Replace with your Formspree endpoint ─────────────────────────────────────
// 1. Go to https://formspree.io and create a free account
// 2. Create a new form, set the destination email to dru@aquastays.com
// 3. Copy the form ID (e.g. "xpwzabcd") and paste it below
const FORMSPREE_ID = "mbdeyeoe";
const FORMSPREE_URL = `https://formspree.io/f/${FORMSPREE_ID}`;
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL = { name: "", email: "", phone: "", checkin: "", checkout: "", guests: "", message: "" };

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-[0.65rem] uppercase tracking-[0.18em] text-white/40 mb-2">{label}</label>
      {children}
      {error && <p className="mt-1.5 text-[0.65rem] text-red-400">{error}</p>}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      className={`w-full bg-white/5 border border-white/10 text-white text-sm font-light px-4 py-3
        placeholder:text-white/20 focus:outline-none focus:border-[#C8A96E]/60 transition-colors ${className}`}
      {...props}
    />
  );
}

export default function ContactSection() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Please add a message";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStatus("sending");
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || "—",
          checkin: form.checkin || "—",
          checkout: form.checkout || "—",
          guests: form.guests || "—",
          message: form.message,
        }),
      });
      if (res.ok) {
        setStatus("success");
        setForm(INITIAL);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#1C1C1A] text-white">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* ── Left: heading + contact details ────────────────────────────── */}
          <div className="lg:col-span-4">
            <p className="eyebrow text-white/50 mb-5">Get in touch</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-white tracking-tight leading-tight">
              We'd love to<br />
              <em className="italic text-[#C8A96E]">hear from you.</em>
            </h2>
            <p className="mt-7 text-white/60 leading-relaxed text-sm font-light">
              Drop Dru a line — we reply within a few hours, morning to evening, 7 days a week.
            </p>

            {/* Contact links */}
            <div className="mt-10 space-y-0">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="group flex items-center justify-between py-5 border-b border-white/10 hover:border-white/25 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Mail size={15} strokeWidth={1.5} className="text-[#C8A96E]" />
                  <div>
                    <p className="text-[0.6rem] uppercase tracking-[0.18em] text-white/35 mb-0.5">Email</p>
                    <p className="text-sm text-white">{CONTACT.email}</p>
                  </div>
                </div>
                <ArrowRight size={13} className="text-white/25 group-hover:text-white/50 group-hover:translate-x-1 transition-all" />
              </a>

              <a
                href={`tel:${CONTACT.phoneIntl}`}
                data-testid="contact-phone-link"
                className="group flex items-center justify-between py-5 border-b border-white/10 hover:border-white/25 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Phone size={15} strokeWidth={1.5} className="text-[#C8A96E]" />
                  <div>
                    <p className="text-[0.6rem] uppercase tracking-[0.18em] text-white/35 mb-0.5">Phone</p>
                    <p className="text-sm text-white">{CONTACT.phone}</p>
                  </div>
                </div>
                <ArrowRight size={13} className="text-white/25 group-hover:text-white/50 group-hover:translate-x-1 transition-all" />
              </a>

              <div className="py-5 border-b border-white/10">
                <p className="text-[0.6rem] uppercase tracking-[0.18em] text-white/35 mb-3">WhatsApp</p>
                <a
                  href="https://wa.me/353876622682?text=Aquastays%20Website%20Booking%20Enquiry"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="inline-block opacity-90 hover:opacity-100 transition-opacity"
                >
                  <img
                    src="https://res.cloudinary.com/dczcnlx82/image/upload/v1781093225/WhatsAppButtonGreenSmall_yz8tro.png"
                    alt="Chat on WhatsApp"
                    className="h-10 w-auto"
                  />
                </a>
              </div>

              <div className="flex items-center gap-4 py-5">
                <MapPin size={15} strokeWidth={1.5} className="text-[#C8A96E] shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase tracking-[0.18em] text-white/35 mb-0.5">Location</p>
                  <p className="text-sm text-white leading-relaxed">{CONTACT.location}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: enquiry form ─────────────────────────────────────────── */}
          <div className="lg:col-span-7 lg:col-start-6">

            {/* Success state */}
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-20 border border-white/10">
                <CheckCircle size={40} strokeWidth={1} className="text-[#C8A96E] mb-6" />
                <h3 className="font-serif text-3xl font-light text-white mb-3">Message received.</h3>
                <p className="text-white/60 text-sm font-light max-w-sm">
                  Thanks for reaching out. Dru will be in touch within a few hours.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 text-[0.65rem] uppercase tracking-[0.18em] text-[#C8A96E] hover:text-white transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <p className="eyebrow text-white/50 mb-8">Enquiry form</p>

                {/* Row 1: Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <Field label="Your name *" error={errors.name}>
                    <Input
                      type="text"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={set("name")}
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Email address *" error={errors.email}>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      value={form.email}
                      onChange={set("email")}
                      autoComplete="email"
                    />
                  </Field>
                </div>

                {/* Row 2: Phone + Guests */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <Field label="Phone (optional)">
                    <Input
                      type="tel"
                      placeholder="+353 87 ..."
                      value={form.phone}
                      onChange={set("phone")}
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="Number of guests">
                    <select
                      value={form.guests}
                      onChange={set("guests")}
                      className="w-full bg-white/5 border border-white/10 text-sm font-light px-4 py-3
                        focus:outline-none focus:border-[#C8A96E]/60 transition-colors appearance-none
                        text-white [&>option]:bg-[#1C1C1A] [&>option]:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="1">1 guest</option>
                      <option value="2">2 guests</option>
                    </select>
                  </Field>
                </div>

                {/* Row 3: Check-in + Check-out */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <Field label="Preferred check-in">
                    <Input
                      type="date"
                      value={form.checkin}
                      onChange={set("checkin")}
                      min={new Date().toISOString().split("T")[0]}
                      className="[color-scheme:dark]"
                    />
                  </Field>
                  <Field label="Preferred check-out">
                    <Input
                      type="date"
                      value={form.checkout}
                      onChange={set("checkout")}
                      min={form.checkin || new Date().toISOString().split("T")[0]}
                      className="[color-scheme:dark]"
                    />
                  </Field>
                </div>

                {/* Row 4: Message */}
                <div className="mb-8">
                  <Field label="Message *" error={errors.message}>
                    <textarea
                      rows={5}
                      placeholder="Tell us a little about your stay — any questions, special occasions, or anything you'd like us to know."
                      value={form.message}
                      onChange={set("message")}
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-light px-4 py-3
                        placeholder:text-white/20 focus:outline-none focus:border-[#C8A96E]/60 transition-colors resize-none"
                    />
                  </Field>
                </div>

                {/* Error banner */}
                {status === "error" && (
                  <div className="flex items-center gap-3 mb-6 px-4 py-3 border border-red-400/30 text-red-400 text-xs">
                    <AlertCircle size={14} strokeWidth={1.5} />
                    Something went wrong. Please try emailing us directly at {CONTACT.email}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-accent w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={13} />
                      Send enquiry
                    </>
                  )}
                </button>

                <p className="mt-4 text-[0.62rem] text-white/25 font-light">
                  We'll reply within a few hours. Prefer to book directly?{" "}
                  <a href="#booking" className="text-[#C8A96E] hover:text-white transition-colors underline underline-offset-2">
                    Use the booking widget above.
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
